import { useCallback, useEffect, useRef, useState } from 'react'
import type { BulkFilesParams } from './bulk-files'
import type { AuthBulkFileListingResponse } from '@ideon-api/sdk-internal'
import { sdkErrorMessage, useAuthServiceClient } from './sdk-client'

export type BulkFilesData = AuthBulkFileListingResponse['data']

export type BulkFilesState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; data: BulkFilesData }

type Answer = { tag: string } & ({ status: 'error'; message: string } | { status: 'ready'; data: BulkFilesData })

// `null` params means idle: no request. A state tagged with another request counts as loading,
// so a late answer or another set's data never shows.
export function useBulkFiles(key: string, params: BulkFilesParams | null): BulkFilesState & { retry: () => void } {
  const getClient = useAuthServiceClient()
  const [answer, setAnswer] = useState<Answer | null>(null)
  const [attempt, setAttempt] = useState(0)
  const tag = params ? JSON.stringify([key, params]) : null

  useEffect(() => {
    if (!tag) return
    const [setKey, folder] = JSON.parse(tag) as [string, BulkFilesParams]
    let cancelled = false
    getClient()
      .then((client) => client.auth.listPermissionSetFiles(setKey, folder))
      .then((response) => !cancelled && setAnswer({ tag, status: 'ready', data: response.data }))
      .catch(
        (error: unknown) =>
          !cancelled && setAnswer({ tag, status: 'error', message: sdkErrorMessage(error, 'Failed to load files') })
      )
    return () => {
      cancelled = true
    }
  }, [getClient, tag, attempt])

  const retry = useCallback(() => setAttempt((n) => n + 1), [])
  if (!tag) return { status: 'idle', retry }
  if (answer?.tag !== tag) return { status: 'loading', retry }
  return { ...answer, retry }
}

const defaultAssign = (url: string) => window.location.assign(url)

// The presigned URL goes straight to `assign` and is never held in state.
export function useBulkFileDownload(key: string, { assign = defaultAssign }: { assign?: (url: string) => void } = {}) {
  const getClient = useAuthServiceClient()
  const [pendingName, setPendingName] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const busy = useRef(false)

  const download = useCallback(
    async (folder: BulkFilesParams, name: string) => {
      if (busy.current) return
      busy.current = true
      setError(null)
      setPendingName(name)
      try {
        const response = await (await getClient()).auth.downloadPermissionSetFile(key, { ...folder, name })
        assign(response.url)
      } catch (e) {
        setError(sdkErrorMessage(e, 'Download failed'))
      } finally {
        busy.current = false
        setPendingName(null)
      }
    },
    [getClient, key, assign]
  )

  return { download, pendingName, error }
}

export type ArchiveState =
  | { status: 'idle' }
  | { status: 'working'; polls: number; maxPolls: number }
  | { status: 'failed'; message: string }

interface ArchiveOptions {
  assign?: (url: string) => void
  sleep?: (ms: number) => Promise<void>
  intervalMs?: number
  // 60 polls at 2s is 2 minutes; the server reads a pending archive older than 15 minutes as failed.
  maxPolls?: number
}

const defaultSleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

// Requests a zip of the folder, polls until it is ready or failed (bounded), then opens the link.
export function useBulkFileArchive(
  key: string,
  { assign = defaultAssign, sleep = defaultSleep, intervalMs = 2000, maxPolls = 60 }: ArchiveOptions = {}
) {
  const getClient = useAuthServiceClient()
  const [state, setState] = useState<ArchiveState>({ status: 'idle' })
  // Bumped by reset and unmount: an older run sees the change and stops without touching state.
  const generation = useRef(0)
  const running = useRef(false)

  useEffect(
    () => () => {
      generation.current += 1
    },
    []
  )

  const reset = useCallback(() => {
    generation.current += 1
    running.current = false
    setState({ status: 'idle' })
  }, [])

  const start = useCallback(
    async (folder: BulkFilesParams) => {
      if (running.current) return
      running.current = true
      const mine = ++generation.current
      const live = () => generation.current === mine
      setState({ status: 'working', polls: 0, maxPolls })
      try {
        const api = (await getClient()).auth
        let archive = await api.createPermissionSetFileArchive(key, folder)
        for (let polls = 0; archive.status === 'pending'; polls++) {
          if (!live()) return
          if (polls >= maxPolls) {
            setState({ status: 'failed', message: 'The zip is taking too long. Try again in a few minutes.' })
            return
          }
          setState({ status: 'working', polls, maxPolls })
          await sleep(intervalMs)
          if (!live()) return
          archive = await api.getPermissionSetFileArchive(key, archive.archiveId)
        }
        if (!live()) return
        if (archive.status === 'ready' && archive.url) {
          assign(archive.url)
          setState({ status: 'idle' })
        } else {
          setState({ status: 'failed', message: 'The zip could not be built. Try again.' })
        }
      } catch (e) {
        if (live()) setState({ status: 'failed', message: sdkErrorMessage(e, 'Could not request the zip') })
      } finally {
        if (live()) running.current = false
      }
    },
    [getClient, key, assign, sleep, intervalMs, maxPolls]
  )

  return { state, start, reset }
}
